import { useEffect, useRef } from 'react'
import { ContentAdminConfig } from 'config'
import { contentServiceOrigin } from './content-url'

type TokenProvider = () => Promise<string>
export type ContentAnnouncement = {
	id: string
	title: string
	path: string
	summary: string
	kind: 'monthly' | 'feature'
}
const notification = 'cg-kb:announcement-seen'
const validId = (id: unknown): id is string =>
	typeof id === 'string' && id.length > 0 && id.length <= 200

const request = async (getToken: TokenProvider, releaseId?: string) => {
	const origin = contentServiceOrigin(ContentAdminConfig.url, window.location.origin)
	const token = await getToken()
	const response = await fetch(`${origin}/reader/announcements`, {
		method: releaseId ? 'POST' : 'GET',
		cache: 'no-store',
		headers: {
			Authorization: `Bearer ${token}`,
			...(releaseId ? { 'Content-Type': 'application/json' } : {})
		},
		...(releaseId ? { body: JSON.stringify({ releaseId }) } : {}),
		signal: AbortSignal.timeout(10000)
	})
	if (!response.ok)
		throw new Error('The seen receipt could not be saved. Try again.')
	return response.json()
}

export const getContentAnnouncement = async (
	getToken: TokenProvider
): Promise<ContentAnnouncement | null> => {
	const { release } = await request(getToken)
	if (
		!release ||
		!validId(release.id) ||
		typeof release.title !== 'string' ||
		typeof release.path !== 'string' ||
		!/^\/docs\/changelog\/[a-z0-9-]+$/.test(release.path)
	)
		return null
	return {
		id: release.id,
		title: release.title,
		path: release.path,
		// Accept the previous API shape during a rolling deployment.
		summary: typeof release.summary === 'string' ? release.summary : '',
		kind: release.kind === 'feature' ? 'feature' : 'monthly'
	}
}

/** Same-origin, same-identity notification; only server receipts decide eligibility. */
const announceSeen = (releaseId: string, subject?: string) => {
	if (!validId(subject)) return
	const receipt = { releaseId, subject }
	window.dispatchEvent(new CustomEvent(notification, { detail: receipt }))
	let channel: BroadcastChannel | undefined
	try {
		channel = new BroadcastChannel(notification)
		channel.postMessage(receipt)
	} catch {
		// Older/private browsers can still inform their other same-origin tabs.
		try {
			localStorage.setItem(notification, JSON.stringify(receipt))
			localStorage.removeItem(notification)
		} catch {
			// A saved server receipt must not depend on browser storage.
		}
	} finally {
		channel?.close()
	}
}

export const subscribeContentAnnouncementSeen = (
	subject: string | undefined,
	onSeen: (releaseId: string) => void
) => {
	if (!validId(subject)) return () => {}
	const receive = (data: any) => {
		if (data?.subject === subject && validId(data?.releaseId))
			onSeen(data.releaseId)
	}
	const local = (event: Event) => receive((event as CustomEvent).detail)
	const storage = (event: StorageEvent) => {
		if (event.key !== notification || !event.newValue) return
		try {
			receive(JSON.parse(event.newValue))
		} catch {
			// Ignore unrelated/malformed notifications.
		}
	}
	let channel: BroadcastChannel | undefined
	try {
		channel = new BroadcastChannel(notification)
		channel.onmessage = (event) => receive(event.data)
	} catch {
		// The storage event is the compatibility fallback.
	}
	window.addEventListener(notification, local)
	window.addEventListener('storage', storage)
	return () => {
		channel?.close()
		window.removeEventListener(notification, local)
		window.removeEventListener('storage', storage)
	}
}

export const saveContentAnnouncementSeen = async (
	getToken: TokenProvider,
	releaseId: string,
	subject?: string
) => {
	if (!validId(releaseId)) throw new Error('Invalid release receipt.')
	const data = await request(getToken, releaseId)
	if (data.saved !== true)
		throw new Error('The seen receipt could not be saved. Try again.')
	announceSeen(releaseId, subject)
}

/** A direct/new-tab changelog visit counts only once its authenticated iframe is ready. */
export const useChangelogAnnouncementSeen = (
	ready: boolean,
	readerPath: string | undefined,
	getToken: TokenProvider,
	subject?: string
) => {
	const activeSubject = useRef(subject)
	activeSubject.current = subject
	const changelog = ready && /^\/docs\/changelog(?:\/|\?|$)/.test(readerPath || '')
	useEffect(() => {
		if (!changelog || !ContentAdminConfig.url) return
		let cancelled = false
		const currentToken = async () => {
			const token = await getToken()
			if (cancelled || activeSubject.current !== subject)
				throw new Error('The reader identity changed.')
			return token
		}
		getContentAnnouncement(currentToken)
			.then(release => {
				if (!cancelled && release)
					return saveContentAnnouncementSeen(currentToken, release.id, subject)
			})
			.catch(() => {
				// Read access/navigation must not depend on a telemetry receipt.
			})
		return () => { cancelled = true }
	}, [changelog, getToken, subject])
}
